import { Input, Label } from "@drs-ops/ui";
import { FormSelect } from "@drs-ops/ui/forms";
import { useQuery } from "@tanstack/react-query";
import { useAdminApi } from "@/hooks/useAdminApi";
import type { CrewMember } from "@/types/crewMember";
import type { OriginRequest } from "@/types/tripPlanner";

export interface OriginSelectorProps {
  value: OriginRequest;
  onChange: (v: OriginRequest) => void;
  homeOfficeLabel: string;
}

export function OriginSelector({ value, onChange, homeOfficeLabel }: OriginSelectorProps): React.ReactElement {
  const api = useAdminApi();
  const crewQuery = useQuery({
    queryKey: ["crew"],
    queryFn: () => api.get<CrewMember[]>("/api/crew"),
  });

  const activeCrew = (crewQuery.data ?? []).filter((c) => c.is_active === 1);

  const selectValue =
    value.type === "home_office" ? "home_office" : value.type === "crew" ? `crew:${value.id}` : "other";

  const handleChange = (v: string) => {
    if (v === "home_office") onChange({ type: "home_office" });
    else if (v === "other") onChange({ type: "address", address: "" });
    else if (v.startsWith("crew:")) onChange({ type: "crew", id: v.slice(5) });
  };

  const options = [
    { value: "home_office", label: homeOfficeLabel },
    ...activeCrew
      .filter((c) => c.home_address !== null)
      .map((c) => ({ value: `crew:${c.id}`, label: `${c.name} — ${c.home_address}` })),
    { value: "other", label: "Other address…" },
  ];

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="origin">Origin</Label>
      <FormSelect id="origin" value={selectValue} onChange={handleChange} options={options} />
      {value.type === "address" && (
        <Input
          placeholder="Other origin address"
          value={value.address}
          onChange={(e) => onChange({ type: "address", address: e.target.value })}
        />
      )}
    </div>
  );
}
